/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * NLS (Natural Language Search) Types
 * Types for the conversational search by natural language query
 */

// ─── Money/Amount Types ─────────────────────────────────────────────────────

export interface NLSMoneyAmount {
  amount: number;
  currency: string;
  decimalPoint: number;
}

// ─── LLM Metadata Types ─────────────────────────────────────────────────────

export interface NLSCallTimes {
  dateCall?: number;
  locationCall?: number;
  priceCall?: number;
  queryRewrite?: number;
}

export interface NLSDateInfo {
  earliest?: string;
  latest?: string;
  length?: number;
  type?: 'EXACT' | 'FLEXIBLE' | string;
}

export interface NLSLocationRecommendation {
  city?: string;
  country?: string;
  latitude?: number;
  locationName?: string;
  longitude?: number;
  state?: string;
}

export interface NLSLocationInfo {
  intent?: string;
  recommendations?: NLSLocationRecommendation[];
}

export interface NLSPriceInfo {
  currency?: string;
  maximum?: number;
  minimum?: number;
}

export interface NLSLlmInfo {
  queryRewrite?: string;
  callTimes?: NLSCallTimes;
  date?: NLSDateInfo;
  location?: NLSLocationInfo;
  price?: NLSPriceInfo;
}

export interface NLSLlmMetadata {
  isLlmEngaged?: boolean;
  llmInfo?: NLSLlmInfo;
}

// ─── Pagination Types ───────────────────────────────────────────────────────

export interface NLSPageInfo {
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  previousOffset?: number;
  currentOffset?: number;
  nextOffset?: number;
}

// ─── Facet Types ────────────────────────────────────────────────────────────

export interface NLSFacetType {
  code: string;
  label?: string;
  count?: number;
  description?: string;
}

export interface NLSRangeFacetBucket {
  index?: number;
  start?: number;
  end?: number;
  count?: number;
}

export interface NLSTermFacetBucket {
  code?: string;
  label?: string;
  description?: string;
  count?: number;
}

export type NLSFacetBucket = NLSRangeFacetBucket | NLSTermFacetBucket;

export interface NLSFacet {
  type: NLSFacetType;
  buckets?: NLSFacetBucket[];
}

// ─── Status Types ───────────────────────────────────────────────────────────

export interface NLSStatusMessage {
  code?: string;
  devMessage?: string;
  message?: string;
}

export interface NLSResponseStatus {
  __typename?: string;
  code?: string;
  httpStatus?: number;
  errors?: NLSStatusMessage[];
  warnings?: NLSStatusMessage[];
}

// ─── Property Types ─────────────────────────────────────────────────────────

export interface NLSBrandPhoto {
  content?: {
    alternateText?: string;
    index?: number;
    name?: string;
    url?: string;
  };
  type?: {
    code?: string;
  };
}

export interface NLSBrand {
  id?: string;
  name?: string;
  type?: string;
  photos?: NLSBrandPhoto[];
}

export interface NLSDescription {
  text?: string;
  type?: {
    code?: string;
    enumCode?: string;
  };
}

export interface NLSBasicInformation {
  isMax?: boolean;
  resort?: boolean;
  isAdultsOnly?: boolean;
  brand?: NLSBrand;
  name?: string;
  nameInDefaultLanguage?: string;
  descriptions?: NLSDescription[];
  currency?: string;
  isRecentlyRenovated?: boolean;
  isFullyRenovated?: boolean;
  hasRenovatedRooms?: boolean;
  newLobby?: boolean;
  newProperty?: boolean;
  openingDate?: string;
  latitude?: number;
  longitude?: number;
  bookable?: boolean;
  hasUniquePropertyLogo?: boolean;
}

export interface NLSReviews {
  stars?: { count?: number };
  numberOfReviews?: { count?: number };
}

export interface NLSImageUrls {
  wideHorizontal?: string;
  square?: string;
  classicHorizontal?: string;
}

export interface NLSMedia {
  primaryImage?: {
    edges?: Array<{
      node?: {
        title?: string;
        imageUrls?: NLSImageUrls;
      };
    }>;
  };
}

export interface NLSProperty {
  id: string;
  basicInformation?: NLSBasicInformation;
  reviews?: NLSReviews;
  media?: NLSMedia;
}

// ─── Rate Types ─────────────────────────────────────────────────────────────

export interface NLSCashAndPointsPerUnit {
  amount?: NLSMoneyAmount;
  amountPlusMandatoryFees?: NLSMoneyAmount;
  fees?: NLSMoneyAmount;
  mandatoryFees?: NLSMoneyAmount;
  points?: number;
  taxes?: NLSMoneyAmount;
  totalAmount?: NLSMoneyAmount;
}

export interface NLSLowestAverageRate {
  amount?: NLSMoneyAmount;
  fees?: NLSMoneyAmount;
  taxes?: NLSMoneyAmount;
  totalAmount?: NLSMoneyAmount;
  amountPlusMandatoryFees?: NLSMoneyAmount;
  mandatoryFees?: NLSMoneyAmount;
}

export interface NLSPointsPerUnit {
  points?: number;
}

export interface NLSRateModes {
  cashAndPointsPerUnit?: NLSCashAndPointsPerUnit;
  lowestAverageRate?: NLSLowestAverageRate;
  pointsPerUnit?: NLSPointsPerUnit;
}

export interface NLSRates {
  rateModes?: NLSRateModes[];
  membersOnly?: boolean;
  lengthOfStay?: number;
  status?: { code?: string };
  rateCategory?: { code?: string; value?: string };
  sourceOfRate?: string;
}

// ─── GeoCode Types ──────────────────────────────────────────────────────────

export interface NLSGeoCodeLocation {
  address?: string;
  city?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  state?: string;
}

export interface NLSGeoCodeDetails {
  destinationType?: string;
  distance?: number;
  location?: NLSGeoCodeLocation;
}

export interface NLSGeoCodeInfo {
  details?: NLSGeoCodeDetails | null;
  placeId?: string | null;
}

// ─── AmpRequest Types ───────────────────────────────────────────────────────

export interface NLSAmpAgentInfo {
  agencyProfileId?: string | null;
  agentType?: string | null;
}

export interface NLSAmpRateRequest {
  rateRequestTypeEnum?: string | null;
  rateRequestValue?: string | null;
}

export interface NLSAmpPriceModifiers {
  rateRequest?: NLSAmpRateRequest[];
}

export interface NLSAmpTimespan {
  startDate?: string | null;
  endDate?: string | null;
}

export interface NLSAmpGuestCount {
  age?: number | null;
  count?: number | null;
}

export interface NLSAmpBaseHospitalityFacilityQuery {
  numberOfRooms?: number | null;
  timespan?: NLSAmpTimespan | null;
  guestCounts?: NLSAmpGuestCount[] | null;
  numberOfNights?: number | null;
}

export interface NLSAmpRoomStayQueryProduct {
  baseHospitalityFacilityQuery?: NLSAmpBaseHospitalityFacilityQuery | null;
}

export interface NLSAmpProductQueryHospitality {
  agentInfo?: NLSAmpAgentInfo | null;
  altCustId?: string | null;
  includeMandatoryFees?: boolean | null;
  includeTaxesAndFees?: boolean | null;
  priceModifiers?: NLSAmpPriceModifiers | null;
  roomStayQueryProduct?: NLSAmpRoomStayQueryProduct | null;
}

export interface NLSAmpRequest {
  productQueriesHospitality?: NLSAmpProductQueryHospitality | null;
}

// ─── Search Result Node ─────────────────────────────────────────────────────

export interface NLSSearchResultNode {
  distance?: number;
  property?: NLSProperty;
  rates?: NLSRates;
}

export interface NLSSearchResultEdge {
  node?: NLSSearchResultNode;
}

// ─── Main Response Type ─────────────────────────────────────────────────────

export interface NLSSearchByNaturalLanguageQueryResult {
  llmMetadata?: NLSLlmMetadata;
  pageInfo?: NLSPageInfo;
  total?: number;
  facets?: NLSFacet[];
  status?: NLSResponseStatus;
  edges?: NLSSearchResultEdge[];
  geoCodeInfo?: NLSGeoCodeInfo;
  nlpEntities?: NLPEntitiesMap | null;
  ampRequest?: NLSAmpRequest;
  fusionSearchId?: string;
}

export interface NLSConversationalSearchResponse {
  searchByNaturalLanguageQuery?: NLSSearchByNaturalLanguageQueryResult;
}

export interface NLSSearchResponse {
  conversationalSearch?: NLSConversationalSearchResponse;
}

export interface NLSGraphQLResponse {
  data?: {
    search?: NLSSearchResponse;
  };
  errors?: any[];
}

// ─── Structured Search Input Types ─────────────────────────────────────────

/**
 * A single entry in search.options.rateRequestTypes.
 * Mirrors the shape used by SearchResultsComponent / submitSearchRequestHelper.
 */
export interface NLSRateRequestType {
  type: string;
  value: string;
}

/**
 * Structured options passed alongside the natural-language query.
 * Maps to the `search.options` field in the GraphQL variables.
 */
export interface NLSSearchOptions {
  startDate?: string | null;
  endDate?: string | null;
  quantity?: number;
  numberInParty?: number;
  childAges?: number[] | null;
  rateRequestTypes?: NLSRateRequestType[] | null;
  includeMandatoryFees?: boolean;
  includeTaxesAndFees?: boolean;
  includeUnavailableProperties?: boolean;
  customerId?: string | null;
}

export interface NLSSearchInput {
  query: string;
  options?: NLSSearchOptions;
  /** Facet filters — same shape as shop's search.facets (terms + ranges) */
  facets?: NLSFacetsPayload;
}

export interface NLSSearchVariables {
  search: NLSSearchInput;
  limit?: number;
  offset?: number;
  filter?: string[];
}

// ─── Search Parameters from UI ──────────────────────────────────────────────

export interface NLSSearchParams {
  /**
   * Base query - sourced from payload.query (SSE top-level field) or
   * llmMetadata.llmInfo.queryRewrite as a fallback
   */
  baseQuery: string;
  /** Check-in date in YYYY-MM-DD format */
  checkInDate?: string;
  /** Check-out date in YYYY-MM-DD format */
  checkOutDate?: string;
  /** Number of rooms */
  roomsCount?: number;
  /** Number of adults per room */
  adultsPerRoom?: number;
  /** Number of children per room */
  childrenPerRoom?: number;
  /** Ages of children */
  childrenAges?: number[];
  /** Use points for redemption */
  usePoints?: boolean;
  /** Selected brand codes */
  brands?: string[];
  /** Selected amenity codes */
  amenities?: string[];
  /** Price range filter */
  priceRange?: {
    min?: number;
    max?: number;
    currency?: string;
  };
  /**
   * Selected facet dimensions map — keyed by facet type (e.g. 'AMENITIES', 'BRANDS', 'PRICE').
   * Each value is a set-like object: { 'pool': 'pool', 'spa': 'spa' }.
   * Built from the filter UI and passed directly to buildNLSFacetsPayload().
   */
  selectedFacets?: NLSSelectedFacetMap;
  /** Special rate codes */
  specialRates?: {
    clusterCode?: string;
    /** Canonical rate selection key (e.g. 'aaa', 'senior', 'govt', 'corp', 'none') */
    specialRateCode?: string;
    corporateCode?: string;
    groupCode?: string;
  };
  /** Include unavailable properties in search results (default: true) */
  includeUnavailableProperties?: boolean;
  /** Pagination limit */
  limit?: number;
  /** Pagination offset */
  offset?: number;
}

// ─── Facet Filter Types ─────────────────────────────────────────────────────

export type NLSFacetCode =
  | 'amenities'
  | 'brand'
  | 'price'
  | 'points'
  | 'distance'
  | 'transportTypes'
  | 'propertyType'
  | 'starRating';

export interface NLSFacetFilter {
  code: NLSFacetCode;
  values: string[] | number[];
}

export interface NLSAppliedFilters {
  facets?: NLSFacetFilter[];
  priceRange?: { min?: number; max?: number };
  distanceRange?: { min?: number; max?: number };
}

// ─── GraphQL Facet Payload (same shape as shop's SearchFacetsType) ───────────

/**
 * A single term or range facet entry in the GraphQL search.facets payload.
 * Mirrors shop's CommonInputValueType — used by both NLS queries.
 */
export interface NLSFacetDimension {
  type: string;
  dimensions?: Array<string | number>;
  endpoints?: Array<string | number>;
}

/**
 * The facets object passed inside $search to the NLS GraphQL queries.
 * Mirrors shop's SearchFacetsType: { terms[], ranges[] }.
 */
export interface NLSFacetsPayload {
  terms: NLSFacetDimension[];
  ranges: NLSFacetDimension[];
}

/**
 * Map of selected facet dimensions keyed by facet type (e.g. AMENITIES, BRANDS).
 * Each key holds a set-like object { 'pool': 'pool', 'spa': 'spa' }.
 * Mirrors shop's SearchStateStoreDataType filter maps.
 */
export type NLSSelectedFacetMap = Record<string, Record<string, string | number>>;

/** Price range endpoint values — mirrors shop's SearchRangesEndPointArr */
export const NLS_PRICE_ENDPOINTS = ['0', '100', '200', 'overflow'];
/** Distance range endpoint values — mirrors shop's SearchDistanceRangesEndPointArr */
export const NLS_DISTANCE_ENDPOINTS = ['0', '4830', '14520', '80470'];
/** Points range endpoint values — mirrors shop's SearchPointsEndPointArr */
export const NLS_POINTS_ENDPOINTS = ['0', '40000', '80000', '120000', '160000', '200000', '240000', 'overflow'];

// ─── NLP Entities (from TIP/NLS nlpEntities field) ──────────────────────────

/**
 * A single NLP entity inside one of the nlpEntities category arrays.
 *
 * Actual shape from TIP response (nlpEntities is an OBJECT, not an array):
 *   nlpEntities = {
 *     "amenities":      [{ label: "Pool",   code: "pool",   action: "filter" }],
 *     "property-types": [{ label: "Resort", code: "resort", action: "boost"  }],
 *     "hotel-types":    [{ label: "Resort",                 action: "boost"  }],
 *     "countries":      [{ label: "Italy",  code: "IT",     action: "boost"  }],
 *     ...
 *   }
 *
 * When action is 'boost' or 'filter' AND `code` is present, the entity should
 * be pre-selected in the filter modal.
 */
export interface NLPEntity {
  /** Human-readable label */
  label: string;
  /** Bucket code used for filter selection (may be absent for some entity types) */
  code?: string;
  /** 'boost' = soft ranking signal, 'filter' = hard filter; only 'filter' is shown as pre-selected */
  action?: 'boost' | 'filter' | string;
}

/**
 * The `nlpEntities` field on the NLS response — an object keyed by
 * lowercase-hyphenated facet category name, each containing an array of NLPEntity.
 *
 * Keys mirror SearchFacetsListCodes values (e.g. 'amenities', 'property-types').
 */
export type NLPEntitiesMap = Record<string, NLPEntity[]>;
